import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";

/**
 * PartnerMediaManager — inline gallery + upload widget used on the Course Edit page.
 * courseId lets you associate uploads with a specific course so they can be served
 * publicly on the course page.
 */
export default function PartnerMediaManager({ courseId }) {
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/api/partner/media").then(({ data }) => {
      // Show only those linked to this course (or all if no courseId)
      const filtered = courseId ? data.filter((a) => a.course_id === courseId) : data;
      setAssets(filtered);
    });

  useEffect(() => { load(); }, [courseId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (courseId) form.append("course_id", courseId);
      await api.post("/api/partner/media", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ${a.filename}?`)) return;
    try {
      await api.delete(`/api/partner/media/${a.id}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const copyUrl = async (a) => {
    const abs = `${window.location.origin}${a.url}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(abs);
      } else {
        const ta = document.createElement("textarea");
        ta.value = abs;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("URL copied");
    } catch (err) {
      toast.error("Copy failed — " + abs);
    }
  };

  return (
    <div className="space-y-3" data-testid="partner-media-manager">
      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" onChange={onFile}
          data-testid="partner-media-file-input"
          accept="image/*,application/pdf,video/mp4,video/quicktime,video/webm"
          className="text-sm" />
        {busy && <span className="text-sm text-gray-500">Uploading…</span>}
      </div>
      {assets.length === 0 ? (
        <div className="text-sm text-gray-500" data-testid="partner-media-empty">
          No media uploaded for this course yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {assets.map((a) => (
            <Card key={a.id} data-testid="partner-media-tile">
              <CardContent className="p-3 space-y-2">
                {a.kind === "image" && (
                  <img src={`${API_BASE.replace(/\/api$/, "")}${a.url}`}
                    alt={a.filename} className="w-full h-24 object-cover rounded"
                    data-testid="partner-media-thumb" />
                )}
                {a.kind === "video" && (
                  <div className="w-full h-24 bg-gray-900 rounded grid place-items-center text-white text-xs">
                    ▶ VIDEO
                  </div>
                )}
                {a.kind === "document" && (
                  <div className="w-full h-24 bg-red-50 rounded grid place-items-center text-red-800 text-xs">
                    PDF
                  </div>
                )}
                <div className="text-xs truncate" title={a.filename}>{a.filename}</div>
                <div className="flex items-center justify-between gap-1">
                  <Badge variant="secondary" className="text-[10px]">{a.size_mb} MB</Badge>
                  <div className="flex gap-1">
                    <button data-testid="partner-media-copy-btn"
                      onClick={() => copyUrl(a)}
                      className="text-xs text-blue-600 hover:underline">Copy URL</button>
                    <button data-testid="partner-media-delete-btn"
                      onClick={() => del(a)}
                      className="text-xs text-red-600 hover:underline">Delete</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
